import util from 'preact-util';
import { compareCursorKeys } from './feedCursor';

export function feedStateLocalKey(hostname, userId) {
    return `feed-state:v1:${hostname}:${userId || 'anon'}`;
}

export function parseLocalFeedState(value) {
    if (!value) return null;
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            articleCursorKey: typeof parsed.articleCursorKey === 'string' ? parsed.articleCursorKey : null,
            newsCursorKey: typeof parsed.newsCursorKey === 'string' ? parsed.newsCursorKey : null,
            articleId: Number(parsed.articleId) || null,
            newsId: Number(parsed.newsId) || null,
            acknowledgedAt: parsed.acknowledgedAt || null,
        };
    } catch (error) {
        return null;
    }
}

export function mergeFeedStates(localState, serverState) {
    if (!localState && !serverState) return null;
    const local = parseLocalFeedState(localState) || {};
    const server = parseLocalFeedState(serverState) || {};
    const articleFromLocal = compareCursorKeys(local.articleCursorKey, server.articleCursorKey) > 0;
    const newsFromLocal = compareCursorKeys(local.newsCursorKey, server.newsCursorKey) > 0;
    return {
        articleCursorKey: articleFromLocal ? local.articleCursorKey : server.articleCursorKey || local.articleCursorKey || null,
        newsCursorKey: newsFromLocal ? local.newsCursorKey : server.newsCursorKey || local.newsCursorKey || null,
        articleId: articleFromLocal
            ? local.articleId
            : server.articleId || (server.articleCursorKey ? null : local.articleId) || null,
        newsId: newsFromLocal
            ? local.newsId
            : server.newsId || (server.newsCursorKey ? null : local.newsId) || null,
        acknowledgedAt: local.acknowledgedAt || server.acknowledgedAt || null,
    };
}

function safeStorage(inputStorage) {
    if (inputStorage) return inputStorage;
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
}

export function createFeedStateClient({
    hostname,
    userId,
    fetchApi = util.fetchApi,
    storage: inputStorage,
}) {
    const storage = safeStorage(inputStorage);
    const key = feedStateLocalKey(hostname, userId);

    const readLocal = () => {
        try {
            return parseLocalFeedState(storage?.getItem(key));
        } catch (error) {
            return null;
        }
    };

    const writeLocal = (state) => {
        try {
            storage?.setItem(key, JSON.stringify(state));
        } catch (error) {
            // Feed rendering must not depend on storage availability.
        }
        return state;
    };

    const patchServer = async (state) => {
        if (!userId || (!state.articleId && !state.newsId)) return state;
        const sendPatch = body => fetchApi('/api/feedstates/', { method: 'PATCH', publish: false }, {
            hostname,
            ...body,
        });
        try {
            const payload = {
                articleId: state.articleId || undefined,
                newsId: state.newsId || undefined,
            };
            const response = await sendPatch(payload);
            if (response?.status === 200) {
                return writeLocal(mergeFeedStates(state, response.data));
            }
            if (state.articleId && state.newsId) {
                let merged = state;
                for (const sourcePayload of [
                    { articleId: state.articleId },
                    { newsId: state.newsId },
                ]) {
                    const sourceResponse = await sendPatch(sourcePayload);
                    if (sourceResponse?.status === 200) {
                        merged = mergeFeedStates(merged, sourceResponse.data);
                    }
                }
                return writeLocal(merged);
            }
        } catch (error) {
            // Local acknowledgement remains authoritative until the next retry.
        }
        return state;
    };

    const load = async () => {
        const local = readLocal();
        if (!userId) return local;
        try {
            const response = await fetchApi('/api/feedstates/', { method: 'GET', publish: false }, { hostname });
            if (response?.status !== 200) return local;
            const merged = writeLocal(mergeFeedStates(local, response.data));
            const localAhead = local && (
                compareCursorKeys(local.articleCursorKey, response.data?.articleCursorKey) > 0
                || compareCursorKeys(local.newsCursorKey, response.data?.newsCursorKey) > 0
            );
            if (localAhead) {
                patchServer(merged);
            }
            return merged;
        } catch (error) {
            return local;
        }
    };

    const acknowledge = async ({
        articleId,
        newsId,
        articleCursorKey,
        newsCursorKey,
    }) => {
        const local = readLocal();
        const candidate = {
            articleCursorKey: articleCursorKey || local?.articleCursorKey || null,
            newsCursorKey: newsCursorKey || local?.newsCursorKey || null,
            articleId: articleId || local?.articleId || null,
            newsId: newsId || local?.newsId || null,
            acknowledgedAt: new Date().toISOString(),
        };
        const merged = writeLocal(mergeFeedStates(local, candidate));
        patchServer(merged);
        return merged;
    };

    return { key, readLocal, load, acknowledge };
}
